import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { getAuth, onAuthStateChanged, User } from "firebase/auth";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "./firebase";
import Login from "./components/Login";
import Register from "./components/Register";
import MainScreen from "./components/MainScreen";
import DayPage from "./components/DayPage";
import ProtectedRoute from "./components/ProtectedRoute";
import AccessDenied from "./components/AccessDenied";
import AugustCountdownPage from "./components/AugustCountdownPage";
import { DailyQuestion } from "./components/DailyQuestion";
import IntroVideo from "./components/IntroVideo";
import LoadingOverlay from "./components/LoadingOverlay";
import PasswordPrompt from "./components/PasswordPrompt";

import { triviaQuestions } from "./constants/triviaQuestions";
import { getCurrentDate, getCurrentMonth, getCurrentDayOfMonth, getTodayDay } from "./utils/currentDate";

const App: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAugust, setIsAugust] = useState<boolean | null>(null);
  const [triviaAnswers, setTriviaAnswers] = useState<
    Record<string, { answered: boolean; correct: boolean; answer: string | number; timestamp: string }>
  >({});
  const [showTrivia, setShowTrivia] = useState(false);
  const [progress, setProgress] = useState<any>(null);
  const [hasSeenIntroVideo, setHasSeenIntroVideo] = useState<boolean>(false);
  const [showReplayIntro, setShowReplayIntro] = useState<boolean>(false);
  const [hasEnteredPassword, setHasEnteredPassword] = useState(false);

  // Navigation loading overlay state
  const [navLoading, setNavLoading] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setNavLoading(true);
    const timeout = setTimeout(() => setNavLoading(false), 400); // Adjust duration as needed
    return () => clearTimeout(timeout);
  }, [location]);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return;
    const fetchIsAugust = async () => {
      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (userDoc.exists()) {
        setIsAugust(userDoc.data().isAugust);
      }
    };
    fetchIsAugust();
  }, [user]);

  const fetchTriviaAnswers = async () => {
    if (!user) return;
    const userDoc = await getDoc(doc(db, "users", user.uid));
    if (userDoc.exists()) {
      setTriviaAnswers(userDoc.data().triviaAnswers || {});
    }
  };

  useEffect(() => {
    if (!user) return;
    fetchTriviaAnswers();
  }, [user, showTrivia]); // Add showTrivia as a dependency

  useEffect(() => {
    if (!user) return; // <-- Only run if user is authenticated
    const fetchUserProgress = async () => {
      const progressDoc = await getDoc(doc(db, "userProgress", user.uid));
      if (progressDoc.exists()) {
        setProgress(progressDoc.data());
      }
    };
    fetchUserProgress();
  }, [user, isAugust]);

  useEffect(() => {
    if (isAugust && triviaAnswers) {
      const month = getCurrentMonth();
      const day = String(getCurrentDayOfMonth()); // Always use string
      // Only show trivia if it's August and today's question is NOT answered
      if (month === 7 && !(triviaAnswers[day] && triviaAnswers[day].answered)) {
        setShowTrivia(true);
      } else {
        setShowTrivia(false);
      }
    }
  }, [isAugust, triviaAnswers]);

  const handleTriviaAnswered = async (correct: boolean, totalCorrect: number) => {
    setShowTrivia(false);

    if (user) {
      const userId = user.uid;
      const updatedAnswers = {
        ...triviaAnswers,
        [String(getCurrentDayOfMonth())]: { answered: true, correct, answer: "", timestamp: new Date().toISOString() },
      };
      setTriviaAnswers(updatedAnswers);

      // Add error handling here
      const userRef = doc(db, "users", userId);
      try {
        await updateDoc(userRef, { triviaAnswers: updatedAnswers });
      } catch (error) {
        console.error("Trivia update Firestore error:", error);
      }
    }
  };

  console.log({ loading, user, isAugust, showTrivia }); // <-- Added debug log

  useEffect(() => {
    setShowTrivia(false);
  }, [user]);

  // Fetch hasEnteredPassword and hasSeenIntroVideo from Firestore
  useEffect(() => {
    if (!user) return;
    const fetchUserFlags = async () => {
      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (userDoc.exists()) {
        setHasEnteredPassword(userDoc.data().hasEnteredPassword ?? false);
        setHasSeenIntroVideo(userDoc.data().hasSeenIntroVideo ?? false);
      }
    };
    fetchUserFlags();
  }, [user, isAugust]);

  // Handler for successful password entry
  const handlePasswordSuccess = async () => {
    setHasEnteredPassword(true);
    if (user) {
      await updateDoc(doc(db, "users", user.uid), { hasEnteredPassword: true });
    }
  };

  // Handler for finishing the intro video
  const handleIntroVideoFinish = async () => {
    setHasSeenIntroVideo(true);
    setShowReplayIntro(false);
    if (user) {
      await updateDoc(doc(db, "users", user.uid), { hasSeenIntroVideo: true });
    }
  };

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (navLoading) {
      timeout = setTimeout(() => setNavLoading(false), 3000); // 3 seconds
    }
    return () => clearTimeout(timeout);
  }, [navLoading]);

  if (loading) {
    console.log("Render: Loading");
    return <div>Loading...</div>;
  }

  if (!user) {
    console.log("Render: Login/Register");
    return (
      <Routes>
        <Route path="/" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    );
  }

  if (isAugust === null) {
    console.log("Render: Waiting for isAugust");
    return <div>Loading...</div>;
  }

  if (!isAugust) {
    console.log("Render: Countdown");
    return <AugustCountdownPage />;
  }

  // Show password prompt if it's August and user hasn't entered the password
  if (isAugust && !hasEnteredPassword && !showReplayIntro) {
    return <PasswordPrompt onSuccess={handlePasswordSuccess}/>;
  }

  // Show intro video if it's August, user hasn't seen it, and not replaying
  if (isAugust && !hasSeenIntroVideo && !showReplayIntro) {
    return <IntroVideo onFinish={handleIntroVideoFinish} />;
  }
  // Show replay video if requested
  if (showReplayIntro) {
    return <IntroVideo onFinish={() => setShowReplayIntro(false)} />;
  }

  if (!triviaAnswers) return <div>Loading...</div>;
  const todayKey = String(getTodayDay());
  if (!triviaAnswers[todayKey]?.answered) {
    return (
      <DailyQuestion
        userId={user.uid}
        onClose={() => setShowTrivia(false)}
        onAnswered={fetchTriviaAnswers}
      />
    );
  }

  console.log("Render: Calendar UI");
  return (
    <>
      <LoadingOverlay visible={navLoading} />
      <Routes>
        <Route path="/" element={<Navigate to="/main" />} />
        <Route path="/main" element={<ProtectedRoute><MainScreen isAugust={isAugust} onReplayIntro={() => setShowReplayIntro(true)} /></ProtectedRoute>} />
        <Route
          path="/day/:day"
          element={
            <ProtectedRoute>
              <DayPage setNavLoading={setNavLoading} />
            </ProtectedRoute>
          }
        />
        <Route path="/access-denied" element={<AccessDenied />} />
        <Route path="*" element={<Navigate to="/main" />} />
      </Routes>
    </>
  );
};

export default App;